import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideOver, SlideOverFooter } from './SlideOver';
import { Input } from './Input';
import { TextArea } from './TextArea';
import { FormField } from './FormField';

interface PriceIndexDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    designation: string;
    factor: number;
    effectiveFrom: string;
    description?: string;
  }) => Promise<void>;
}

export default function PriceIndexDialog({ isOpen, onClose, onSubmit }: PriceIndexDialogProps) {
  const { t } = useTranslation(['services', 'common']);
  const [designation, setDesignation] = useState('');
  const [factor, setFactor] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!designation.trim()) {
      newErrors.designation = t('services:priceIndex.designationRequired');
    }

    const factorNum = parseFloat(factor);
    if (!factor || isNaN(factorNum) || factorNum <= 0) {
      newErrors.factor = t('services:priceIndex.factorError');
    }

    if (!effectiveFrom) {
      newErrors.effectiveFrom = t('services:priceIndex.validFromRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        designation,
        factor: parseFloat(factor),
        effectiveFrom,
        description: description.trim() || undefined,
      });

      // Reset form
      setDesignation('');
      setFactor('');
      setEffectiveFrom('');
      setDescription('');
      setErrors({});
      onClose();
    } catch (error) {
      console.error(t('services:priceIndex.createError'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setDesignation('');
    setFactor('');
    setEffectiveFrom('');
    setDescription('');
    setErrors({});
    onClose();
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={handleClose}
      title={t('services:priceIndex.title')}
      footer={
        <SlideOverFooter
          onCancel={handleClose}
          onConfirm={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
          confirmText={t('services:buttons.create')}
          loading={isSubmitting}
        />
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          label={t('services:priceIndex.designation')}
          required
          error={errors.designation}
        >
          <Input
            value={designation}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDesignation(e.target.value)}
            placeholder={t('services:priceIndex.designationPlaceholder')}
            disabled={isSubmitting}
          />
        </FormField>

        <FormField
          label={t('services:priceIndex.factor')}
          required
          error={errors.factor}
          helperText={t('services:priceIndex.factorHelp')}
        >
          <Input
            type="number"
            step="0.01"
            min="0.01"
            value={factor}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFactor(e.target.value)}
            placeholder={t('services:priceIndex.factorPlaceholder')}
            disabled={isSubmitting}
          />
        </FormField>

        <FormField
          label={t('services:priceIndex.validFrom')}
          required
          error={errors.effectiveFrom}
        >
          <Input
            type="date"
            value={effectiveFrom}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEffectiveFrom(e.target.value)}
            disabled={isSubmitting}
          />
        </FormField>

        <FormField label={t('services:priceIndex.description')}>
          <TextArea
            label=""
            value={description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
            placeholder={t('services:priceIndex.descriptionPlaceholder')}
            rows={3}
            disabled={isSubmitting}
          />
        </FormField>
      </form>
    </SlideOver>
  );
}
